// Tiny external store with selector hooks (zustand-like, no dependency).
// const s = createStore({ n: 0 }); s.set({ n: 1 }); const n = useStore(s, (x) => x.n);
import { useEffect, useRef, useState } from 'preact/hooks';

export interface Store<T> {
  get(): T;
  /** Shallow-merge a patch (or the result of an updater) into the state and notify subscribers. */
  set(patch: Partial<T> | ((s: T) => Partial<T>)): void;
  /** Replace the whole state. */
  replace(next: T): void;
  subscribe(fn: (s: T, prev: T) => void): () => void;
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const subs = new Set<(s: T, prev: T) => void>();
  const emit = (prev: T) => subs.forEach((fn) => fn(state, prev));
  return {
    get: () => state,
    set(patch) {
      const p = typeof patch === 'function' ? patch(state) : patch;
      const prev = state;
      state = Object.assign({}, state, p);
      emit(prev);
    },
    replace(next) { const prev = state; state = next; emit(prev); },
    subscribe(fn) { subs.add(fn); return () => { subs.delete(fn); }; },
  };
}

/** Shallow equality for selector results (objects/arrays compared one level deep). */
export function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a as object), kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (!Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false;
  return true;
}

/** Subscribe a component to a slice of a store. Re-renders only when the selected value changes (shallowly). */
export function useStore<T extends object, S>(store: Store<T>, select: (s: T) => S, equal: (a: S, b: S) => boolean = shallowEqual): S {
  const selRef = useRef(select);
  selRef.current = select;
  const [value, setValue] = useState(() => select(store.get()));
  const valRef = useRef(value);
  valRef.current = value;
  useEffect(() => {
    const check = () => {
      const next = selRef.current(store.get());
      if (!equal(valRef.current, next)) { valRef.current = next; setValue(() => next); }
    };
    check(); // the store may have changed between render and effect
    return store.subscribe(check);
  }, [store]);
  // Always return the freshest value for this render (selector may depend on props).
  const fresh = select(store.get());
  return equal(fresh, value) ? value : fresh;
}
